import type { Device } from "@rhema/client";
import { safetyNumber } from "@rhema/crypto";
import { Card, Eyebrow, ShieldCheckIcon } from "@rhema/ui";
import { useEffect, useState } from "react";
import { firstName } from "../data/view";
import { useClient } from "../session/session";

type Row = { device: Device; groups: string[] };

/**
 * One safety number for each of someone's devices, from your identity key and theirs.
 * Both people see the same 60 digits when no one is in the middle.
 */
export function SafetyNumbers({ userId, name }: { userId: string; name: string }) {
  const client = useClient();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    (async () => {
      const devices = await client.crypto.devicesOf(userId, true);
      const out = await Promise.all(
        devices.map(async (device) => ({
          device,
          groups: (await safetyNumber(client.crypto.ed25519, device.ed25519)).split(" "),
        })),
      );
      if (live) setRows(out);
    })().catch(() => live && setError("The safety number could not be worked out. Try again later."));
    return () => {
      live = false;
    };
  }, [client, userId]);

  const first = firstName(name);
  return (
    <Card className="px-4 py-3.5">
      <div className="flex items-center gap-2">
        <ShieldCheckIcon size={18} strokeWidth={2.2} className="text-olive" />
        <h2 className="m-0 text-[15px] font-bold">Safety number</h2>
      </div>
      <p className="m-0 mt-1.5 text-[13.5px] leading-normal text-ink-secondary">
        Compare these numbers with the ones {first} sees on their screen, in person or on a call. If every
        group matches, your messages to that device are end-to-end encrypted and no one is in between.
      </p>
      {error && <p className="m-0 mt-3 text-sm text-scarlet">{error}</p>}
      {!rows && !error && <p className="m-0 mt-3 text-sm text-ink-meta">Working out the safety number.</p>}
      {rows?.length === 0 && (
        <p className="m-0 mt-3 text-sm text-ink-meta">
          {first} has not signed in on any device yet, so there is nothing to compare.
        </p>
      )}
      {rows?.map(({ device, groups }) => (
        <section key={device.id} aria-label={`Safety number for ${device.name}`} className="mt-3.5">
          <Eyebrow>{device.name || "Device"}</Eyebrow>
          <ol className="select-text m-0 mt-1.5 grid list-none grid-cols-4 gap-x-3 gap-y-1 p-0 font-semibold text-[15px] tabular-nums tracking-[0.06em]">
            {groups.map((g, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: groups are positional
              <li key={i}>{g}</li>
            ))}
          </ol>
        </section>
      ))}
    </Card>
  );
}
